import { useEffect, useRef } from 'react';
import type { TaskCollectionChangedNotification } from 'types/pushNotifications';

export type TaskCollectionChangeReason = 'created' | 'deleted' | 'membership' | 'reconnected';

export interface TaskCollectionChange {
  key: string;
  reason: TaskCollectionChangeReason;
  taskId?: string;
}

type TaskCollectionChangeListener = (change: TaskCollectionChange) => void;
type TaskCollectionRefreshListener = (notification: TaskCollectionChangedNotification) => void;

const listeners = new Set<TaskCollectionChangeListener>();
const pendingChanges = new Map<string, TaskCollectionChange>();
const taskCollectionRefreshListeners = new Set<TaskCollectionRefreshListener>();

export const TASK_COLLECTION_REFRESH_TIMEOUT_MS = 30_000;

export type TaskCollectionRefreshResult = 'refreshed' | 'timed-out';

export const emitTaskCollectionChange = (change: TaskCollectionChange) => {
  pendingChanges.set(change.key, change);
  listeners.forEach((listener) => listener(change));
};

export const clearTaskCollectionChanges = () => pendingChanges.clear();

export const emitTaskCollectionRefresh = (notification: TaskCollectionChangedNotification) => {
  taskCollectionRefreshListeners.forEach((listener) => listener(notification));
};

export const waitForTaskCollectionRefresh = (
  campaignId: string,
  contractId?: string,
  timeoutMs = TASK_COLLECTION_REFRESH_TIMEOUT_MS
) => {
  let timeout: ReturnType<typeof setTimeout>;
  let resolveRefresh!: (result: TaskCollectionRefreshResult) => void;
  const promise = new Promise<TaskCollectionRefreshResult>((resolve) => {
    resolveRefresh = resolve;
  });
  const cancel = () => {
    clearTimeout(timeout);
    taskCollectionRefreshListeners.delete(listener);
  };
  const listener: TaskCollectionRefreshListener = (notification) => {
    if (notification.campaignId !== campaignId || notification.contractId !== contractId) return;

    cancel();
    resolveRefresh('refreshed');
  };

  taskCollectionRefreshListeners.add(listener);
  timeout = setTimeout(() => {
    cancel();
    resolveRefresh('timed-out');
  }, timeoutMs);

  return {
    promise,
    cancel,
  };
};

export const useTaskCollectionChanges = (onTaskCollectionChanged: TaskCollectionChangeListener) => {
  const listenerRef = useRef(onTaskCollectionChanged);

  useEffect(() => {
    listenerRef.current = onTaskCollectionChanged;
  }, [onTaskCollectionChanged]);

  useEffect(() => {
    const listener = (change: TaskCollectionChange) => listenerRef.current(change);

    listeners.add(listener);
    pendingChanges.forEach(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);
};
